import { dispatcher } from 'dispatcher';
import { useMemo } from 'react';
import { useChangePlayerFavorites, useGetPlayerFavorites } from 'server';

import * as hooks from './hooks';
import { useSetExcludedGameCodes } from 'js/hooks/games';

export const useGamePublicAPI = () => {
    const getGamesConfiguration = hooks.useGetGamesConfiguration();
    const getPlayerFavorites = useGetPlayerFavorites();
    const changePlayerFavorites = useChangePlayerFavorites();
    const setExcludedGameCodes = useSetExcludedGameCodes();
    const getOldGamesFeeds = hooks.useGetOldGamesFeeds();
    const getGamesFeed = hooks.useGetGamesFeed();

    return useMemo(() => ({
        /**
         * Open a game according to launch configuration of its game type, if it exists.
         * Otherwise, do nothing.
         * @memberOf Playtech.API.game
         * @function
         * @param {Object} options
         * @param {string} options.code game code
         * @param {number} [options.mode = 1] mode
         * @example
         * Playtech.API.game.open({ code: 'gameCode' });
         */
        open: ({ code, mode = 1 }: { code: string; mode?: 0 | 1 }) => dispatcher.dispatch('game:launch', { code, mode }),
        /**
         * Getting games configuration data.
         * @memberOf Playtech.API.game
         * @function
         * @param {boolean} [disableCache] if true data will always be taken from server instead of storage
         * @returns {Promise<{Object}>} {
         *     games: Object[],
         *     gameTypes: Object[],
         *     basicGameTileLayout: Object,
         *     liveGameTileLayout: Object,
         *     basicGameInfoLayout: Object,
         *     liveGameInfoLayout: Object
         * }
         * @example
         * Playtech.API.game.getGamesConfiguration(true)
         *  .then(({ games, gameTypes, basicGameTileLayout, liveGameTileLayout, basicGameInfoLayout, liveGameInfoLayout }) => {...});
         */
        getGamesConfiguration,
        /**
         * Return player favorites.
         * @deprecated
         * @memberOf Playtech.API.game
         * @function
         * @param {Object} options
         * @param {string[]} options.requestedFavorites list of supported favorites. only 'games' is supported for now.
         * @returns {Promise<{ favoriteGames: string[] }>}
         * @example
         * Playtech.API.game.getPlayerFavorites({ requestedFavorites: ['games'] }).then(() => { ... });
         */
        getPlayerFavorites,
        /**
         * Change player favorites.
         * @memberOf Playtech.API.game
         * @function
         * @param {Object} options
         * @param {('add' | 'remove')} options.requestType type of the operation.
         * @param {string[]} options.gameTypes list of game codes to add to / remove from favorites.
         * @returns {Promise<{ status: string }>}
         * @example
         * Playtech.API.game.changePlayerFavorites({ gameTypes: ['manos'], requestType: 'add' }).then(() => { ... });
         */
        changePlayerFavorites,
        /**
         * Set the list of excluded game codes.
         * @memberOf Playtech.API.game
         * @function
         * @param {Object} options
         * @param {string[]} options.excludedGameCodes list of game codes to exclude from the whole Portal (except dynamic categories).
         * @returns {void}
         * @example
         * Playtech.API.game.setExcludedGameCodes({ excludedGameCodes: ['manos'] });
         */
        setExcludedGameCodes,
        /**
         * Getting all old games feeds.
         * @function
         * @deprecated
         * @memberOf Playtech.API.game
         * @param {boolean} [disableCache] if true data will always be taken from server instead of storage
         * @returns {Promise<feed[]>} {
         *     name: string,
         *     categories: {
         *         cid: string,
         *         name: string,
         *         gameCodes: string[],
         *         roundIcon: string,
         *         pgg2: {
         *             enabled: boolean,
         *             maxGames: number,
         *             minGames: number,
         *         },
         *         displayName: string,
         *         displayNameShort: string
         *     }[],
         *     active: boolean,
         *     translationKeyPrefixMob: string
         * }
         * @example
         * Playtech.API.game.getOldGamesFeeds(true).then(() => { ... });
         */
        getOldGamesFeeds,
        /**
         * Getting games feed.
         * @memberOf Playtech.API.game
         * @function
         * @param {string} feedName
         * @param {boolean} [disableCache] if true data will always be taken from server instead of storage
         * @returns {Promise<{
         *     name: string,
         *     gamesFeedCategories: Object[],
         *     gamesGridLayouts: Object<string, Object>
         * }>}
         * @example
         * Playtech.API.game.getGamesFeed('design-feed', true).then(() => { ... });
         */
        getGamesFeed
    }), [
        getGamesFeed,
        getOldGamesFeeds,
        getPlayerFavorites,
        changePlayerFavorites,
        getGamesConfiguration,
        setExcludedGameCodes
    ]);
};
